/**
 * "You cannot skip past what you have not watched."
 *
 * A learner may scrub freely inside the part of a lesson they have already
 * seen, and nowhere beyond it, until they reach the end once — after which that
 * lesson is unrestricted forever. Off unless `lms_seek_restriction` is on.
 *
 * The whole problem is telling PLAYBACK from a SEEK using only the samples the
 * players give us, which differ per backend: a native `<video>` fires
 * `timeupdate` about 4×/s, Vimeo pings every 500 ms, and YouTube delivers time
 * in bursts that `usePlayer` extrapolates between. None of them expose a
 * trustworthy "the user is seeking" flag, and `PlayerSnapshot` deliberately
 * does not invent one.
 *
 * So the mark advances on ELAPSED WALL-CLOCK TIME instead: between two samples,
 * playback can only have advanced by the time that actually passed, times the
 * playback rate, plus slack. Anything further is a jump. That reasoning holds
 * identically at 2× on YouTube's bursty samples and at 1× on a native element,
 * which is why it is one rule rather than three.
 *
 * Pure and DOM-free so it can be tested against every sampling shape without a
 * player: the enforcement lives in `lesson-player.tsx`.
 */

/** Rate-scaled slack per sample, for jitter and the odd late frame. */
export const SLACK_SECONDS = 2;

/**
 * A gap longer than this credits only this much progress.
 *
 * A backgrounded tab, a paused-and-resumed lesson or a laptop lid closed for an
 * hour must not hand the learner an hour of unwatched content. Ten seconds is
 * comfortably longer than any real sampling gap (the heartbeat itself is 10 s)
 * and short enough that idling is not a bypass.
 */
export const MAX_GAP_SECONDS = 10;

/** After a snap-back, ignore violations this long — stale samples are in flight. */
export const SNAP_QUIET_MS = 1500;

export type SeekState = {
  /** Furthest second watched. Monotonic within a session. */
  maxWatched: number;
  /** Reached the end once: seeking is unrestricted from then on. */
  fullyWatched: boolean;
  /** Timestamp (ms) of the last accepted sample; null before the first. */
  lastAcceptedAt: number | null;
  /** Ignore violations until this timestamp (ms). */
  snapUntil: number | null;
};

export function seedSeekState(progress: {
  maxPositionSeconds?: number | null;
  fullyWatchedAt?: string | null;
}): SeekState {
  return {
    maxWatched: Math.max(0, progress.maxPositionSeconds ?? 0),
    fullyWatched: !!progress.fullyWatchedAt,
    lastAcceptedAt: null,
    snapUntil: null,
  };
}

/**
 * Fold one playback sample into the state.
 *
 * Returns the new state and, when the sample sits beyond what could have been
 * watched, the second the player should be snapped back to. Backward movement
 * is always accepted — rewatching is not skipping.
 */
export function observePlayback(
  state: SeekState,
  sample: {
    currentTime: number;
    /** `Date.now()` at the sample. Injected so tests need no clock control. */
    at: number;
    rate: number;
    playing: boolean;
    ended?: boolean;
  },
): { state: SeekState; snapTo: number | null } {
  const { currentTime, at, rate, playing, ended } = sample;

  if (ended) {
    // The end is the end, whatever route got there: a lesson watched through
    // is unrestricted from now on.
    return {
      state: { ...state, fullyWatched: true, maxWatched: Math.max(state.maxWatched, currentTime) },
      snapTo: null,
    };
  }
  if (state.fullyWatched) {
    return { state: { ...state, lastAcceptedAt: at }, snapTo: null };
  }
  if (!Number.isFinite(currentTime) || currentTime < 0) return { state, snapTo: null };

  // Rewinding, or sitting still, is always fine.
  if (currentTime <= state.maxWatched) {
    return { state: { ...state, lastAcceptedAt: at }, snapTo: null };
  }

  // The first sample of a lesson has nothing to measure against, so it is
  // accepted: the resume seek that precedes it is already clamped, and a
  // learner opening a fresh lesson starts at 0 anyway.
  if (state.lastAcceptedAt == null) {
    return { state: { ...state, maxWatched: currentTime, lastAcceptedAt: at }, snapTo: null };
  }

  const elapsed = Math.max(0, (at - state.lastAcceptedAt) / 1000);
  const credited = Math.min(elapsed, MAX_GAP_SECONDS) * Math.max(rate, 1);
  const allowed = state.maxWatched + credited + SLACK_SECONDS;

  if (currentTime <= allowed) {
    return { state: { ...state, maxWatched: currentTime, lastAcceptedAt: at }, snapTo: null };
  }

  // A jump. While paused there is nothing to snap (the learner may be dragging
  // the scrubber, and the commit is clamped separately), and inside the quiet
  // window a stale sample from before the last snap is still arriving.
  if (state.snapUntil != null && at < state.snapUntil) return { state, snapTo: null };
  if (!playing) return { state, snapTo: state.maxWatched };
  return { state, snapTo: state.maxWatched };
}

/** Start the quiet window after snapping the player back. */
export function markSnap(state: SeekState, now: number): SeekState {
  return { ...state, snapUntil: now + SNAP_QUIET_MS, lastAcceptedAt: now };
}

/**
 * Clamp a seek the APP is about to perform (scrubber, keyboard, a note chip).
 * `blocked` is what drives the "watch to here first" flash.
 */
export function clampSeek(
  target: number,
  state: SeekState,
  enabled: boolean,
): { seconds: number; blocked: boolean } {
  if (!enabled || state.fullyWatched) return { seconds: target, blocked: false };
  if (target <= state.maxWatched) return { seconds: target, blocked: false };
  return { seconds: state.maxWatched, blocked: true };
}

/** The furthest second the scrubber may offer, or null when unrestricted. */
export function seekLimit(state: SeekState, enabled: boolean): number | null {
  if (!enabled || state.fullyWatched) return null;
  return state.maxWatched;
}
